import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { Sucursal } from "@/services/accounts.service";
import { useRoles } from "@/hooks/useRoles";
import { Flex } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import {
  fuelSlotRequestOrderCreationSchema,
  FuelSlotRequestOrderCreationForm,
} from "./schema/fuel-slot-request-order-creation.schema";
import { OrderCard } from "./OrderCard";
import {
  buildDispatchPlaceOptions,
  buildProductOptions,
  createOrdersHandler,
  fetchBranchesHandler,
  fetchProductsHandler,
  handleCancelHandler,
  handleDeleteOrderHandler,
  handleSaveAndCreateAnotherOrderHandler,
  preloadProductsFromDetail,
} from "./schema/handler-functions";
import { fetchDetailHandler } from "../detail/schema/handler-functions";
import { FuelSlotRequestDetail } from "../detail/schema/fuel-slot-request-detail.schema";
import { isEmpty } from "@/utils/query-string";
import { isValidHttpUrl, validateHttpUrl } from "@/utils/request-validations";

export const FuelSlotRequestOrderCreationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number>(0);

  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingCreateOrders, setLoadingCreateOrders] = useState(false);

  const [combustibles, setCombustibles] = useState<
    { id: number; nombre: string }[]
  >([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);

  const {
    control,
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FuelSlotRequestOrderCreationForm>({
    resolver: zodResolver(fuelSlotRequestOrderCreationSchema),
    defaultValues: {
      orders: [
        {
          dispatchPlace: "",
          observations: "",
          URLMaps: "",
          products: [{ productId: "", tons: "" }],
        },
      ],
    },
  });

  const watched = useWatch({ control });

  useEffect(() => {
    const orders = (watched?.orders ?? []) as any[];

    orders.forEach((o, idx) => {
      validateHttpUrl({
        watchedUrl: o?.URLMaps,
        clearErrors: clearErrors as any,
        setError: setError as any,
        fieldName: `orders.${idx}.URLMaps` as any,
      });
    });
  }, [watched?.orders]);

  const {
    fields: orderFields,
    append: appendOrder,
    remove: removeOrder,
    replace: replaceOrders,
  } = useFieldArray({
    control,
    name: "orders",
  });

  const hasNoFilledValues = useMemo(() => {
    const orders = watched?.orders ?? [];
    if (!orders.length) return true;

    const normalizeSingle = (v: any) => {
      if (Array.isArray(v)) return v?.[0];
      return v;
    };

    const parseLiters = (v: any) => {
      const raw = typeof v === "string" ? v : String(v ?? "");
      const trimmed = raw.trim();
      if (!trimmed) return 0;
      const normalized = trimmed.replace(",", ".");
      const parsed = Number.parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    return orders.some((o) => {
      const products = o?.products ?? [];
      const hasInvalidProducts =
        !products.length ||
        products.some((p) => {
          const productId = normalizeSingle(p?.productId);
          const liters = parseLiters(p?.tons);
          return !productId || liters <= 0;
        });

      const dispatchPlace = normalizeSingle(o?.dispatchPlace);
      const url = String(normalizeSingle(o?.URLMaps) ?? "").trim();
      const invalidUrl = !isEmpty(url) && !isValidHttpUrl(url);
      return !dispatchPlace || hasInvalidProducts || isEmpty(url) || invalidUrl;
    });
  }, [watched]);

  const handleCancel = () => {
    handleCancelHandler(navigate, id);
  };

  const onSubmit = async (_values: FuelSlotRequestOrderCreationForm) => {
    setLoadingCreateOrders(true);
    try {
      await createOrdersHandler({
        id,
        values: _values,
        navigate,
        fetchPermissions,
      });
    } finally {
      setLoadingCreateOrders(false);
    }
  };

  const fetchProducts = async () => {
    await fetchProductsHandler({
      navigate,
      fetchPermissions,
      setLoadingProducts,
      setCombustibles,
    });
  };

  const fetchBranches = async () => {
    await fetchBranchesHandler({
      navigate,
      fetchPermissions,
      setLoadingBranches,
      setSucursales,
    });
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (isActive) {
      fetchProducts();
      fetchBranches();
    }
  }, [isActive]);

  const [detail, setDetail] = useState<FuelSlotRequestDetail | null>(null);
  const didPreloadFromDetail = useRef(false);
  const [preloadDone, setPreloadDone] = useState(false);

  useEffect(() => {
    if (!isActive || !id) return;

    fetchDetailHandler({
      id,
      navigate,
      fetchPermissions,
      setLoadingDetail,
      setDetail,
    });
  }, [isActive, id]);

  useEffect(() => {
    preloadProductsFromDetail({
      detail,
      didPreloadFromDetail,
      combustibles,
      replaceOrders,
    });

    if (!preloadDone) {
      const noProductsToPreload =
        detail !== null && !((detail?.productos ?? []).length);

      if (didPreloadFromDetail.current || noProductsToPreload) {
        setPreloadDone(true);
      }
    }
  }, [detail, combustibles, replaceOrders]);

  useEffect(() => {
    if (!id && !preloadDone) setPreloadDone(true);
  }, [id, preloadDone]);

  const globalLoading =
    loadingProducts ||
    loadingBranches ||
    loadingDetail ||
    (!preloadDone && Boolean(id));

  const productOptions = useMemo(() => {
    const normalizeName = (s: any) =>
      String(s ?? "")
        .trim()
        .toLowerCase();

    const catalogIdByName = new Map(
      (combustibles ?? [])
        .filter((c) => Number.isFinite(Number((c as any)?.id)))
        .map((c) => [normalizeName((c as any)?.nombre), Number((c as any)?.id)]),
    );

    const catalogIds = new Set(
      (combustibles ?? [])
        .map((c: any) => Number(c?.id))
        .filter(Number.isFinite),
    );

    const extraProducts = (detail?.productos ?? [])
      .map((p: any) => {
        const nombre = String(p?.nombreProducto ?? p?.nombre ?? "");
        const nameKey = normalizeName(nombre);

        const rawIdProducto =
          p?.idProducto ??
          p?.productoId ??
          p?.idProductoCatalogo ??
          p?.producto?.id;

        const rawFallbackId = p?.id;
        let id = Number(rawIdProducto ?? rawFallbackId ?? 0);

        if (!catalogIds.has(id) && nameKey) {
          const catalogId = catalogIdByName.get(nameKey);
          if (catalogId !== undefined) id = catalogId;
        }

        return {
          id,
          nombre,
        };
      })
      .filter((p: any) => Number.isFinite(p.id) && p.nombre.trim().length);

    return buildProductOptions(combustibles, extraProducts);
  }, [combustibles, detail?.productos]);

  const inactiveProductIds = useMemo(() => {
    return new Set(
      (productOptions ?? [])
        .filter((o: any) => Boolean(o?.isDisabled))
        .map((o: any) => String(o?.value ?? "")),
    );
  }, [productOptions]);

  const hasInactiveSelectedProduct = useMemo(() => {
    const orders = watched?.orders ?? [];
    return orders.some((o: any) =>
      (o?.products ?? []).some((p: any) => {
        const raw = Array.isArray(p?.productId)
          ? p.productId?.[0]
          : p?.productId;
        const v = raw !== undefined && raw !== null ? String(raw) : "";
        return v && inactiveProductIds.has(v);
      }),
    );
  }, [watched?.orders, inactiveProductIds]);

  const dispatchPlaceOptions = useMemo(
    () => buildDispatchPlaceOptions(sucursales),
    [sucursales],
  );

  const handleDeleteOrder = (index: number) => {
    handleDeleteOrderHandler({
      index,
      removeOrder,
      setEditingIndex,
    });
  };

  const handleSaveAndCreateAnotherOrder = () => {
    handleSaveAndCreateAnotherOrderHandler({
      appendOrder,
      setEditingIndex,
      ordersLength: orderFields.length,
    });
  };

  return (
    <Flex
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      height="100%"
      minHeight={0}
    >
      <SectionHeader
        title="Realizar pedido"
        addButtonLabel="Continuar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        addButtonType="submit"
        disableAdd={
          globalLoading ||
          hasNoFilledValues ||
          loadingCreateOrders ||
          hasInactiveSelectedProduct
        }
        loading={globalLoading || loadingCreateOrders}
      />

      <Flex
        flexDir="column"
        gap="16px"
        marginTop="30px"
        overflowY="auto"
        minH={0}
        flex="1"
        paddingRight="8px"
        paddingBottom="8px"
      >
        {orderFields.map((order, orderIndex) => (
          <OrderCard
            key={order.id}
            control={control as any}
            errors={errors as any}
            register={register as any}
            orderIndex={orderIndex}
            isEditing={editingIndex === orderIndex}
            setEditingIndex={setEditingIndex}
            onDeleteOrder={handleDeleteOrder}
            onSaveAndCreateAnotherOrder={handleSaveAndCreateAnotherOrder}
            disableSaveAndCreateAnotherOrder={
              hasInactiveSelectedProduct ||
              isEmpty((watched?.orders as any)?.[orderIndex]?.URLMaps) ||
              (!isEmpty((watched?.orders as any)?.[orderIndex]?.URLMaps) &&
                !isValidHttpUrl((watched?.orders as any)?.[orderIndex]?.URLMaps))
            }
            productOptions={productOptions}
            dispatchPlaceOptions={dispatchPlaceOptions}
            loadingProducts={loadingProducts}
            loadingBranches={loadingBranches}
            orderFieldsLength={orderFields.length}
            watchedOrders={watched?.orders ?? []}
          />
        ))}
      </Flex>
    </Flex>
  );
};
